import { useEffect, useCallback, useMemo } from "react";
import posthog from "posthog-js";
import { useCentralStore } from "../../store/Central";
import { SharedAgendaPage } from "@/components/src/agenda/agenda-page.component";
import {
	AgendaGroup,
	AgendaJobData,
} from "@/components/src/agenda/agenda.types";
import {
	FullScreen,
	useFullScreenHandle,
} from "../../hooks/useFullScreenHandle";
import { HeaderPortal } from "@/components/src/header-portal/header-portal.component";
import {
	SheetContentType,
	useSheetStore,
} from "../../store/Sheets/sheet.store";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { JobStatusEnum } from "@/lib/types/job";
import { Logger } from "@/lib/logger/Logger";
import { useTeamProfilesContext } from "../../context/single-entity-contexts/TeamProfilesContext";
import { getUserColor } from "../../lib/utils/color-utils";

const visibleStatuses = [
	// JobStatusEnum.PRE_REGISTERED,
	JobStatusEnum.REQUESTED,
	JobStatusEnum.IN_PROGRESS,
	JobStatusEnum.COMPLETED,
	JobStatusEnum.BOOKED_SINGLE,
	JobStatusEnum.BOOKED_MONTHLY,
	JobStatusEnum.PAID_SINGLE,
	JobStatusEnum.PAID_MONTHLY,
];

const getBeginningOfLastWeek = () => {
	const d = new Date();
	const dow = d.getDay();
	const daysSinceMonday = dow === 0 ? 6 : dow - 1;
	d.setDate(d.getDate() - daysSinceMonday - 7);
	return d;
};

export const AgendaPage = () => {
	const profile = useCentralStore((s) => s.profile);
	const organization = useCentralStore((s) => s.organization);
	const openSheet = useSheetStore((s) => s.openSheet);
	const navigate = useNavigate();
	const handle = useFullScreenHandle();
	const {
		teamProfiles: { teamProfilesLookup },
	} = useTeamProfilesContext();
	const customColors = (organization?.profile_colors ?? {}) as Record<
		string,
		string
	>;
	const profileColors = useMemo(() => {
		const map: Record<string, string> = {};
		for (const id of Object.keys(teamProfilesLookup)) {
			map[id] = getUserColor(id, customColors);
		}
		return map;
	}, [teamProfilesLookup, customColors]);
	const profilesMap = teamProfilesLookup as Record<
		string,
		{ first_name?: string; last_name?: string }
	>;

	useEffect(() => {
		posthog.capture("agenda_page_viewed");
	}, []);

	const fetchGroups = useCallback(async (): Promise<AgendaGroup[]> => {
		const cutoff = getBeginningOfLastWeek();
		const cutoffISO = cutoff.toISOString().split("T")[0];

		const { data, error } = await supabase.rpc("get_agenda_jobs", {
			p_organization_id: organization!.id!,
			p_statuses: visibleStatuses,
			p_date_from: cutoffISO,
		});

		if (error) {
			Logger.error(error, {}, "Error fetching agenda jobs");
			return [];
		}

		if (!data) return [];

		const grouped: Record<string, AgendaGroup> = {};
		for (const row of data as any[]) {
			const clientId = String(row.client_id);
			if (!grouped[clientId]) {
				grouped[clientId] = {
					id: clientId,
					name: row.client_name || "",
					jobs: [],
				};
			}
			grouped[clientId].jobs.push({
				id: row.id,
				code: row.code,
				title: row.title,
				status: row.status,
				is_untouched: row.is_untouched,
				incoming_order_date: row.incoming_order_date,
				outgoing_order_date: row.outgoing_order_date,
				incoming_order_time: row.incoming_order_time,
				outgoing_order_time: row.outgoing_order_time,
				patient_first_name: row.patient_first_name,
				patient_last_name: row.patient_last_name,
				client_name: row.client_name,
				client_id: clientId,
				delivery_courier_type: row.delivery_courier_type,
				delivery_profile_id: row.delivery_profile_id,
				delivery_courier_company_name:
					row.delivery_courier_company_name,
			});
		}

		return Object.values(grouped).filter((g) => g.jobs.length > 0);
	}, [organization?.id]);

	const handleJobClick = useCallback(
		(job: AgendaJobData) => {
			openSheet(SheetContentType.JOB, {
				job,
				onShareFile: async () => {},
				onUnshareFile: async () => {},
				onAcceptJobRequest: async () => ({ success: false }),
				onDenyJobRequest: async () => false,
			});
		},
		[openSheet]
	);

	const handleJobNavigate = useCallback(
		(groupId: string, jobId: number) => {
			navigate(`/client/${groupId}/job/${jobId}`);
		},
		[navigate]
	);

	if (!profile || !organization) return null;

	return (
		<FullScreen className="w-full h-full" handle={handle}>
			<SharedAgendaPage
				organizationId={organization.id!}
				profileId={profile.id}
				appId="dentlab"
				fetchGroups={fetchGroups}
				onJobClick={handleJobClick}
				onJobNavigate={handleJobNavigate}
				renderHeader={(title) => <HeaderPortal>{title}</HeaderPortal>}
				fullScreenHandle={{
					active: handle.active,
					enter: handle.enter,
				}}
				profileColors={profileColors}
				profilesMap={profilesMap}
			/>
		</FullScreen>
	);
};
